import { Box, Flex, Heading, VStack, Text } from '@chakra-ui/react';
import { UserContentLayout } from '../../components/Layouts/UserContentLayout';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FormInput } from '../../components/Form/FormInput';
import { FormSelect } from '../../components/Form/FormSelect';
import { API_ROUTERS, ROUTERS, USER_GENDER_OPTIONS } from '../../data';
import { FormDateInput } from '../../components/Form/FormDataInput';
import { FormPhoneInput } from '../../components/Form/FormPhoneInput';
import {
  CREATE_AND_EDIT_MEMBER_DEFAULT_VALUES,
  ICreateAndEditMemberForm,
  CREATE_AND_EDIT_MEMBER_VALIDATION,
} from '../../utils/forms/CreateAndEditMember';
import { yupResolver } from '@hookform/resolvers/yup';
import { dateFormatForReq, formValidateByRes } from '../../services';
import { useRequest, useToast, useToggle } from '../../hooks';
import { MemberConfirmDeleting } from '../../components/Modals/MemberConfirmDeleting';
import { InvitationBlock } from '../../components/User/Member/InvitationBlock';
import { FormFooter } from '../../components/Form/FormFooter';
import { RemoveBtn } from '../../components/Buttons/RemoveBtn';

const CreateAndEditMember = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { member } = state || {};
  const navigate = useNavigate();
  const { authRequest } = useRequest();
  const { isShow, show, hide } = useToggle();
  const { showToast } = useToast();
  const isEditMode = !!member;

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<ICreateAndEditMemberForm>({
    mode: 'onChange',
    defaultValues: member || CREATE_AND_EDIT_MEMBER_DEFAULT_VALUES,
    resolver: yupResolver(CREATE_AND_EDIT_MEMBER_VALIDATION),
  });

  const goBack = () => navigate(ROUTERS.USER.MEMBERS);

  const onSubmit = async (formValues: ICreateAndEditMemberForm) => {
    const { birthday, avatar, ...restFormValues } = formValues;
    const sendData = {
      birthDate: dateFormatForReq(birthday),
      avatar: typeof avatar === 'string' ? null : avatar,
      ...restFormValues,
    };

    try {
      const url = isEditMode ? `${API_ROUTERS.MEMBERS}/${member?.id}` : API_ROUTERS.MEMBERS;
      await authRequest({ method: 'post', url, data: sendData });
      showToast({
        type: 'success',
        message: isEditMode ? `${t('toast.member.success.updated')}` : `${t('toast.member.success.created')}`,
      });
      goBack();
    } catch (e) {
      formValidateByRes(e, setError);
    }
  };

  const onConfirmDeleting = async () => {
    try {
      await authRequest({ method: 'delete', url: `${API_ROUTERS.MEMBERS}/${member?.id}` });
      showToast({ type: 'success', message: `${t('toast.member.success.deleted')}` });
      goBack();
    } catch (e) {
    } finally {
      hide();
    }
  };

  return (
    <>
      <UserContentLayout caption={`${t('members.page.caption')}`}>
        <>
          <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
            <VStack mt={6} maxW='xl' spacing={4} mb={2}>
              <Flex alignItems='center' justifyContent='space-between' w='100%'>
                <Heading fontSize='xl'>
                  {t(isEditMode ? 'member.edit.form.caption' : 'member.create.form.caption')}
                </Heading>

                {isEditMode ? <RemoveBtn onClick={() => show(member)}>{t('member.delete.btn')}</RemoveBtn> : null}
              </Flex>

              <FormInput
                isRequired
                control={control}
                name='firstName'
                label={`${t('input.name.label')}`}
                placeholder={`${t('input.name.placeholder')}`}
              />

              <FormInput
                isRequired
                control={control}
                name='lastName'
                label={`${t('input.last.name.label')}`}
                placeholder={`${t('input.last.name.placeholder')}`}
              />

              {isEditMode ? null : (
                <FormDateInput
                  isRequired
                  control={control}
                  name='birthday'
                  label={`${t('input.birth.date.label')}`}
                  isReadOnly={isEditMode}
                />
              )}

              <FormPhoneInput
                control={control}
                placeholder='xxxxxxxxx'
                name='mobilePhone'
                label={`${t('input.mobile.phone.label')}`}
              />

              <FormSelect
                control={control}
                name='gender'
                label={`${t('input.gender.label')}`}
                placeholder={`${t('input.gender.placeholder')}`}
                options={USER_GENDER_OPTIONS}
              />

              <Text fontSize='xs' w='100%' color='gray.500'>
                <Text color='brand.pink' as='span'>
                  *
                </Text>
                &nbsp;{t('member.edit.mandatory.fields')}
              </Text>

              <Box w='100%'>
                <FormFooter
                  mt={6}
                  submitBtn={{
                    title: `${t(isEditMode ? 'save.changes' : 'member.create.submit.btn')}`,
                    isDisabled: !isValid,
                  }}
                  cancelBtn={{ onClick: goBack }}
                />
              </Box>

              {isEditMode ? (
                <Box pt={{ base: 2, lg: 6 }} w='100%'>
                  <InvitationBlock memberId={member?.id} userName={member?.firstName || t('no.data')} />
                </Box>
              ) : null}
            </VStack>
          </form>
        </>
      </UserContentLayout>

      {/* ──── Modals ──────────────────────────────────────────────────────────────────────────── */}
      <MemberConfirmDeleting
        isOpen={!!isShow}
        onClose={hide}
        onConfirm={onConfirmDeleting}
        useName={isShow?.firstName}
        memberId={isShow?.id}
      />
    </>
  );
};

export default CreateAndEditMember;
